import { ReactElement, useMemo } from 'react';
import { Trans } from 'react-i18next';

import { PatientMedicine } from '@dreryk/egabinet-prescription-frontend-api';
import { MagistralDrug } from '@dreryk/drerykweb-medicine-api';

import { assertUnreachable, Translate, useTranslate } from '@dreryk/react-common';
import { ModalConfirmButton, StrongText, StyledFlex } from '@dreryk/react-components';

import { hasEligibleReimbursementCode, PatientReimbursement } from '~libs.shared.prescription';

import { draftPrescriptionsTranslationKeys } from '../../../../../../translations';
import { DraftPrescriptionField, DraftPrescriptionsFieldArray } from '../../DraftPrescriptionsForm.types';
import { PatientMedicinesWithReimbursementsTable } from '../shared/PatientMedicinesWithReimbursementsTable';
import { DraftMagistralDrugPrescription } from '../../magistralDrugPrescription/DraftMagistralDrugPrescriptionField.types';

const isSelectedByUserMagistralDrug = (
   selectedByUser: DraftPrescriptionField['selectedByUser']
): selectedByUser is Omit<DraftPrescriptionField['selectedByUser'], 'magistralDrug'> & {
   magistralDrug: MagistralDrug;
} => {
   return 'magistralDrug' in selectedByUser;
};

const isSelectedByUserStandardMedicine = (
   selectedByUser: DraftPrescriptionField['selectedByUser']
): selectedByUser is Omit<DraftPrescriptionField['selectedByUser'], 'patientMedicine'> & {
   patientMedicine: PatientMedicine;
   patientReimbursement: PatientReimbursement | undefined;
} => {
   return 'patientMedicine' in selectedByUser;
};
const isPrescriptionWithMagistralDrug = (
   draftPrescriptionField: DraftPrescriptionField
): draftPrescriptionField is DraftMagistralDrugPrescription => {
   return 'medicineName' in draftPrescriptionField.data;
};

export const getMedicineName = (prescriptionField: DraftPrescriptionField): string => {
   const { sourceType } = prescriptionField;

   if (sourceType === 'addedByUser') {
      const { selectedByUser } = prescriptionField;

      if (isSelectedByUserMagistralDrug(selectedByUser)) {
         return selectedByUser.magistralDrug.name;
      }

      if (isSelectedByUserStandardMedicine(selectedByUser)) {
         return selectedByUser.patientMedicine.medicine.fullName;
      }
   }

   if (sourceType === 'fromServer') {
      if (isPrescriptionWithMagistralDrug(prescriptionField)) {
         return prescriptionField.data.medicineName;
      }

      const patientMedicine =
         prescriptionField.initialData.medicinePrescription?.patientMedicine ||
         prescriptionField.selectedByUser.patientMedicine;

      if (patientMedicine) {
         return patientMedicine.medicine.fullName;
      }
   }

   return assertUnreachable(undefined);
};

type PatientEntitlementBenefitsCheckModalContentProps = {
   readonly draftPrescriptions: DraftPrescriptionsFieldArray;
   readonly onConfirm: VoidFunction;
};

export const PatientEntitlementBenefitsCheckModalContent = (
   props: PatientEntitlementBenefitsCheckModalContentProps
): ReactElement => {
   const translate = useTranslate();
   const medicinesWithReimbursements = useMemo(
      () =>
         props.draftPrescriptions.reduce((result, prescription, index) => {
            const reimbursementCode = prescription.data.reimbursement?.code;
            if (hasEligibleReimbursementCode(reimbursementCode)) {
               result.push({
                  reimbursement: translate(draftPrescriptionsTranslationKeys.reimbursements[reimbursementCode]),
                  medicineName: getMedicineName(prescription),
                  id: index,
               });
            }

            return result;
         }, [] as { reimbursement: string; medicineName: string; id: number }[]),
      [props.draftPrescriptions, translate]
   );

   if (medicinesWithReimbursements.length === 1) {
      const [onlyMedicine] = medicinesWithReimbursements;

      return (
         <>
            <Trans
               i18nKey={draftPrescriptionsTranslationKeys.patientNoConfirmedEligibilityBenefitsSingle}
               components={{ bold: <StrongText testId={'emphasis'} /> }}
               values={{
                  reimbursement: onlyMedicine.reimbursement,
                  medicineName: onlyMedicine.medicineName,
               }}
            />
            <ModalConfirmButton
               label={draftPrescriptionsTranslationKeys.sendPrescriptionWithoutEntitlements}
               onClick={props.onConfirm}
            />
         </>
      );
   }

   return (
      <StyledFlex gap={'18px'} direction={'column'}>
         <Translate>{draftPrescriptionsTranslationKeys.patientNoConfirmedEligibilityBenefitsMultiple}</Translate>
         <PatientMedicinesWithReimbursementsTable medicinesWithReimbursements={medicinesWithReimbursements} />
         <Translate>{draftPrescriptionsTranslationKeys.areYouSureSendingPrescriptionWithReimbursement}</Translate>
         <ModalConfirmButton
            label={draftPrescriptionsTranslationKeys.sendPrescriptionsWithoutEntitlements}
            onClick={props.onConfirm}
         />
      </StyledFlex>
   );
};
